"use client";
import { useEffect, useState } from "react";

import {
	Bar,
	BarChart,
	CartesianGrid,
	XAxis,
	YAxis,
	Legend,
	ResponsiveContainer,
} from "recharts";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from "@/components/ui/card";
import {
	type ChartConfig,
	ChartContainer,
	ChartTooltip,
} from "@/components/ui/chart";
import type { Financial } from "@/interfaces/dashboard";

export function Finance({ data }: { data: Financial[] }) {
	if (!Array.isArray(data)) {
		return (
			<Card>
				<CardContent>
					<p>No data available</p>
				</CardContent>
			</Card>
		);
	}

	const chartConfig = {
		income: {
			label: "ລາຍຮັບ",
			color: "#FFE8D6",
		},
		expense: {
			label: "ລາຍຈ່າຍ",
			color: "#5B8E7D",
		},
		cashDirect: {
			label: "ກະແສເງິນສົດທາງກົງ",
			color: "#B7B7A4",
		},
		cashIndirect: {
			label: "ກະແສເງິນສົດທາງອ້ອມ",
			color: "#BC4B51",
		},
	} satisfies ChartConfig;

	// Add this helper function
	const getLabelForKey = (key: string) => {
		return chartConfig[key as keyof typeof chartConfig]?.label || key;
	};

	const formatYAxis = (value: number) => {
		if (value === 0) return "0";
		if (value >= 1000000000000)
			return `${(value / 1000000000000).toFixed(0)} ໂກດ`;
		if (value >= 1000000000) return `${(value / 1000000000).toFixed(0)} ຕື້`;
		if (value >= 1000000) return `${(value / 1000000).toFixed(0)} ລ້ານ`;
		if (value >= 100000) return `${(value / 100000).toFixed(0)} ແສນ`;
		if (value >= 1000) return `${(value / 1000).toFixed(0)} ພັນ`;
		if (value <= -1000000000000)
			return `-${(value / -1000000000000).toFixed(0)} ໂກດ`;
		if (value <= -1000000000) return `-${(value / 1000000000).toFixed(0)} ຕື້`;
		if (value <= -1000000) return `-${(value / 1000000).toFixed(0)} ລ້ານ`;
		if (value <= -100000) return `-${(value / 100000).toFixed(0)} ແສນ`;
		if (value <= -1000) return `-${(value / 1000).toFixed(0)} ພັນ`;
		if (value >= -100 && value < 0) return `-${(value).toFixed(0)}`;
		return value.toFixed(0);
	};

	const formatTooltipValue = (value: number | null) => {
		if (value === null) return "N/A";
		return new Intl.NumberFormat("la-LAOS", {
			style: "currency",
			currency: "LAK",
		}).format(value);
	};
	console.log("Finance Data:", data);
	const [isLoading, setIsLoading] = useState(true);
	const [chartReady, setChartReady] = useState(false);

	useEffect(() => {
		setIsLoading(true);
		try {
			if (data.length > 0) {
				setChartReady(true);
			}
		} catch (error) {
			console.error("Error processing chart data:", error);
		} finally {
			setIsLoading(false);
		}
	}, [data]);

	if (isLoading) {
		return (
			<Card>
				<CardContent>
					<p>Loading...</p>
				</CardContent>
			</Card>
		);
	}
	// Function to generate 10 evenly spaced ticks starting from 500000000
	// const generateTicks = (data: Financial[]) => {
	// 	const minValue = Math.min(...data.map((item) => item.totalAsset));
	// 	const maxValue = Math.max(...data.map((item) => item.totalAsset));
	// 	const range = maxValue - minValue;
	// 	const step = Math.
	// 	round(range / 10);
	// 	const ticks = Array.from({ length: 10 }, (_, index) => {
	// 		const value = minValue + index * step;
	// 		return value;
	// 	});
	const generateTicks = (data) => {
		const maxValue = Math.max(
			...data.flatMap((d) => [
				d.income || 0,
				d.expense || 0,
				d.cashDirect || 0,
				d.cashIndirect || 0,
			]),
		);
		const step = Math.ceil(maxValue / 9); // 9 steps for 10 ticks
		return Array.from({ length: 10 }, (_, i) => 50 + i * step);
	};
	const yAxisTicks = generateTicks(data);
	return (
		<Card>
			<CardHeader>
				<CardTitle>ລາຍງານການເງິນລັດວິສາຫະກິດ</CardTitle>
				<CardDescription>
					ລາຍຈ່າຍທັງໝົດ, ລາຍຮັບທັງໝົດ, ກະແສເງິນສົດທາງອ້ອມ, ກະແສເງິນສົດທາງກົງ
				</CardDescription>
			</CardHeader>
			<CardContent className="pt-4">
				{chartReady && data.length > 0 ? (
					<ChartContainer config={chartConfig} className="w-full h-[500px]">
						<ResponsiveContainer width="100%" height="100%">
							<BarChart
								data={data}
								margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
							>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="yearly" />
								<YAxis
									tickFormatter={formatYAxis}
									scale="linear"
									domain={[0, "auto"]}
								//ticks={yAxisTicks}
								/>
								<Legend />
								<ChartTooltip
									content={({ active, payload, label }) => {
										if (active && payload && payload.length) {
											return (
												<div className="p-2 rounded-lg border shadow-lg bg-background border-border">
													<p className="font-bold">{`ປີ: ${label}`}</p>
													{payload.map((entry, index) => (
														<p
															key={`item-${index}`}
															style={{ color: entry.color }}
														>
															{`${getLabelForKey(
																entry.dataKey as string,
															)}: ${formatTooltipValue(entry.value as number)}`}
														</p>
													))}
												</div>
											);
										}
										return null;
									}}
								/>
								{Object.keys(chartConfig)?.map((key) => (
									<Bar
										key={key}
										dataKey={key}
										name={getLabelForKey(key)}
										fill={chartConfig[key as keyof typeof chartConfig].color}
										radius={[4, 4, 0, 0]}
										maxBarSize={40}
									/>
								))}
							</BarChart>
						</ResponsiveContainer>
					</ChartContainer>
				) : (
					<div className="w-full h-[500px] rounded-lg bg-muted/20 flex flex-col items-center justify-center relative overflow-hidden">
						{/* <div className="absolute inset-0 z-[-10] bg-gradient-to-br from-muted/5 to-muted/30"></div> */}

						{/* Animated bars */}
						<div className="flex absolute right-0 bottom-0 left-0 z-10 justify-around px-10 h-3/4">
							{Array.from({ length: 7 }).map((_, i) => (
								<div
									key={i}
									className="w-8 bg-gradient-to-t rounded-t-md animate-pulse from-primary/40 to-primary/10"
									style={{
										height: `${15 + Math.random() * 60}%`,
										animationDelay: `${i * 0.1}s`,
										opacity: 0.7
									}}
								/>
							))}
						</div>

						{/* Grid lines */}
						<div className="flex absolute inset-0 z-0 flex-col justify-between opacity-30">
							{Array.from({ length: 5 }).map((_, i) => (
								<div key={i} className="w-full border-t border-dashed border-muted-foreground/20" />
							))}
						</div>

						{/* X-axis */}
						<div className="absolute right-0 bottom-0 left-0 h-px bg-border"></div>

						{/* Y-axis */}
						<div className="absolute top-0 bottom-0 left-0 w-px bg-border"></div>

						{/* Message */}
						<div className="z-20 px-6 py-4 rounded-lg border shadow-lg backdrop-blur-sm bg-background/80 border-border">
							<div className="flex gap-2 items-center text-muted-foreground">
								<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-pulse">
									<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
									<line x1="12" y1="9" x2="12" y2="13"></line>
									<line x1="12" y1="17" x2="12.01" y2="17"></line>
								</svg>
								<span className="text-lg font-medium">ບໍ່ມີຂໍ້ມູນສຳລັບສະແດງຜົນ</span>
							</div>
							<p className="mt-2 text-sm text-muted-foreground">ກະລຸນາເພີ່ມຂໍ້ມູນເພື່ອສະແດງຜົນຂອງກຣາຟ</p>
						</div>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
